import { useCallback, useEffect, useRef, useState } from 'react';

import { useUserState } from '@/state';
import { readLastActivity, writeLastActivity } from '@/utils/idleActivity';

// Auto-wylogowanie po bezczynności. Znacznik ostatniej aktywności trzyma
// `utils/idleActivity` (localStorage, wspólny dla zakładek i dla `userState`).

export const IDLE_LIMIT_MS = 60 * 60 * 1000; // wylogowanie po 60 min bezczynności
export const IDLE_WARNING_MS = 2 * 60 * 1000; // ostrzeżenie 2 min przed wylogowaniem

const ACTIVITY_THROTTLE_MS = 30 * 1000; // nie zapisujemy do localStorage częściej niż co 30 s
const CHECK_INTERVAL_MS = 1000; // co sekundę, żeby odliczanie w modalu było płynne

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

interface IdleLogoutResult {
  isWarning: boolean;
  secondsLeft: number;
  stayLoggedIn: () => void;
}

export const useIdleLogout = (): IdleLogoutResult => {
  const { currentUser, logout } = useUserState();
  const [isWarning, setIsWarning] = useState(false);
  const [msLeft, setMsLeft] = useState(IDLE_WARNING_MS);

  const isWarningRef = useRef(false);
  const isLoggingOutRef = useRef(false);

  useEffect(() => {
    isWarningRef.current = isWarning;
  }, [isWarning]);

  const recordActivity = useCallback(() => {
    // Gdy ostrzeżenie jest już na ekranie, sam ruch myszy NIE przedłuża sesji —
    // wymagamy świadomego kliknięcia "Zostań zalogowany".
    if (isWarningRef.current) return;

    const now = Date.now();
    if (now - readLastActivity() < ACTIVITY_THROTTLE_MS) return;
    writeLastActivity(now);
  }, []);

  const stayLoggedIn = useCallback(() => {
    writeLastActivity(Date.now());
    setIsWarning(false);
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setIsWarning(false);
      return undefined;
    }

    isLoggingOutRef.current = false;

    // Zapas, gdyby znacznika nie było (np. sesja przywrócona po reloadzie na świeżo
    // wyczyszczonym localStorage). Po samym logowaniu licznik zeruje `markActivityNow`
    // w `userState` — inaczej znacznik z wczorajszej sesji wylogowywałby od razu.
    if (!readLastActivity()) writeLastActivity(Date.now());

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, recordActivity, { passive: true });
    });

    const check = (): void => {
      const idleFor = Date.now() - readLastActivity();

      if (idleFor >= IDLE_LIMIT_MS) {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;
        setIsWarning(false);
        logout();
        return;
      }

      if (idleFor >= IDLE_LIMIT_MS - IDLE_WARNING_MS) {
        setIsWarning(true);
        setMsLeft(IDLE_LIMIT_MS - idleFor);
      } else {
        // Aktywność mogła przyjść z innej zakładki — wtedy chowamy ostrzeżenie.
        setIsWarning(false);
      }
    };

    check(); // od razu przy starcie — łapie sesję porzuconą np. na noc
    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, recordActivity);
      });
    };
  }, [currentUser, logout, recordActivity]);

  return {
    isWarning,
    secondsLeft: Math.max(0, Math.ceil(msLeft / 1000)),
    stayLoggedIn,
  };
};
